import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useIsMobile } from '@/hooks/useIsMobile';
import type { OverrideEffect } from '@/types/api';

export interface OverrideDraft {
  username: string;
  appKey: string;
  appName: string;
  effect: OverrideEffect;
  reason: string;
  expiresAt?: string | null;
}

interface FormProps {
  draft: OverrideDraft;
  onCancel: () => void;
  onConfirm: (draft: OverrideDraft) => void;
}

/**
 * A reason is mandatory (the API rejects a blank one). That is deliberate: an
 * unexplained override is a mystery for whoever finds it in a year.
 */
function OverrideForm({ draft, onCancel, onConfirm }: FormProps) {
  // Initialised from props once, because the parent remounts this component per
  // draft (see the `key` below). No effect syncing state to props — that pattern
  // causes a cascading re-render and React's own lint rule flags it.
  const [reason, setReason] = useState(draft.reason);
  const [expiresAt, setExpiresAt] = useState(draft.expiresAt?.slice(0, 10) ?? '');

  const allow = draft.effect === 'allow';
  const valid = reason.trim().length > 0;

  return (
    <>
      <DialogTitle>{allow ? 'Zugriff immer erlauben' : 'Zugriff immer sperren'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {draft.username} · {draft.appName}
          </Typography>
          {allow && (
            <Alert severity="info">
              Diese Ausnahme gilt auch dann, wenn der Mandant keine Lizenz für die App hat.
            </Alert>
          )}
          <TextField
            label="Grund"
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
            autoFocus
            helperText="Wird im Protokoll gespeichert."
          />
          <TextField
            label="Läuft ab am (optional)"
            type="date"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Leer lassen für unbefristet."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant="contained"
          color={allow ? 'primary' : 'error'}
          disabled={!valid}
          onClick={() =>
            onConfirm({
              ...draft,
              reason: reason.trim(),
              // A date input yields a local calendar day; the API stores an
              // instant, so pin it to the end of that day in UTC rather than
              // silently expiring the override at midnight local time.
              expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59Z`).toISOString() : null,
            })
          }
        >
          Speichern
        </Button>
      </DialogActions>
    </>
  );
}

export function OverrideDialog({
  draft,
  onCancel,
  onConfirm,
}: {
  draft: OverrideDraft | null;
  onCancel: () => void;
  onConfirm: (draft: OverrideDraft) => void;
}) {
  const isMobile = useIsMobile();

  if (!draft) return null;

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth fullScreen={isMobile}>
      {/* The key remounts the form for each (user, app) pair, so its fields
          initialise from the new draft instead of being synced by an effect. */}
      <OverrideForm
        key={`${draft.username}:${draft.appKey}:${draft.effect}`}
        draft={draft}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    </Dialog>
  );
}
